import { SP_CONFIG } from "../authConfig";

/**
 * Resolve the SharePoint drive ID for the configured site.
 */
export async function getDriveId(client) {
  const site = await client
    .api(`/sites/${SP_CONFIG.hostname}:${SP_CONFIG.sitePath}`)
    .get();
  const drives = await client.api(`/sites/${site.id}/drives`).get();
  // Pick the default "Documents" drive
  const drive = drives.value.find((d) => d.name === "Documents") || drives.value[0];
  return { siteId: site.id, driveId: drive.id };
}

/**
 * List all vendor sub-folders under the PO root folder.
 * Paginated so folder lists over 200 are fully returned.
 */
export async function listVendorFolders(client, driveId) {
  let res = await client
    .api(`/drives/${driveId}/root:/${SP_CONFIG.poRootFolder}:/children`)
    .filter("folder ne null")
    .select("id,name,folder,lastModifiedDateTime")
    .top(999)
    .get();

  let items = res.value || [];
  let next = res["@odata.nextLink"];
  let guard = 0;
  while (next && guard < 50) {
    const page = await client.api(next).get();
    items = items.concat(page.value || []);
    next = page["@odata.nextLink"];
    guard++;
  }

  return items.filter((i) => i.folder);
}

/**
 * List all PDF files inside a specific vendor folder.
 * Paginated so large folders (e.g. ZClosed) return everything,
 * not just the first 200 items.
 */
export async function listPOsForVendor(client, driveId, vendorFolder) {
  const path = `${SP_CONFIG.poRootFolder}/${vendorFolder}`;

  let res = await client
    .api(`/drives/${driveId}/root:/${path}:/children`)
    .expand("listItem($expand=fields($select=Status))")
    .select("id,name,size,lastModifiedDateTime,webUrl,file,listItem")
    .top(999)
    .get();

  let items = res.value || [];
  let next = res["@odata.nextLink"];
  let guard = 0;
  while (next && guard < 50) {
    const page = await client.api(next).get();
    items = items.concat(page.value || []);
    next = page["@odata.nextLink"];
    guard++;
  }

  // Flatten Status onto each item for easy access
  items.forEach((i) => {
    i.fields = i.listItem?.fields || {};
  });

  return items.filter(
    (i) => i.file && i.name.toLowerCase().endsWith(".pdf")
  );
}

/**
 * Upload a PDF file to a vendor folder.
 * Uses the simple upload API (files up to 4 MB).
 * For larger files, use the resumable upload session instead.
 */
export async function uploadPO(client, driveId, vendorFolder, file) {
  const path = `${SP_CONFIG.poRootFolder}/${vendorFolder}/${file.name}`;
  // Pass the File/Blob directly — the Graph SDK handles it in the browser
  // without needing Node's Buffer
  return client
    .api(`/drives/${driveId}/root:/${path}:/content`)
    .header("Content-Type", file.type || "application/pdf")
    .put(file);
}

/**
 * Move a file to a different vendor folder.
 */
export async function movePO(client, driveId, fileId, targetVendorFolder) {
  // Resolve target folder ID
  const targetPath = `${SP_CONFIG.poRootFolder}/${targetVendorFolder}`;
  const targetFolder = await client
    .api(`/drives/${driveId}/root:/${targetPath}`)
    .select("id")
    .get();

  return client.api(`/drives/${driveId}/items/${fileId}`).patch({
    parentReference: { id: targetFolder.id },
  });
}

/**
 * Get a short-lived download URL for a file (for PDF preview).
 */
export async function getDownloadUrl(client, driveId, fileId) {
  const item = await client
    .api(`/drives/${driveId}/items/${fileId}`)
    .select("@microsoft.graph.downloadUrl,webUrl")
    .get();
  return item["@microsoft.graph.downloadUrl"] || item.webUrl;
}

// ─── Comments (stored in a SharePoint List) ──────────────────────────────────

/**
 * Ensure the "PO Comments" list exists; create it if not.
 */
export async function ensureCommentsList(client, siteId) {
  try {
    await client
      .api(`/sites/${siteId}/lists/${SP_CONFIG.commentsListName}`)
      .get();
  } catch {
    await client.api(`/sites/${siteId}/lists`).post({
      displayName: SP_CONFIG.commentsListName,
      list: { template: "genericList" },
      columns: [
        { name: "POFileName", text: {} },
        { name: "VendorFolder", text: {} },
        { name: "CommentText", text: { allowMultipleLines: true } },
        { name: "AuthorName", text: {} },
        { name: "MentionedEmails", text: {} },
      ],
    });
  }
}

/**
 * Fetch all comments for a given PO file.
 * Paginated — the list holds comments for every PO, so the total
 * can exceed a single page even when one PO has only a few.
 */
export async function getComments(client, siteId, poFileName) {
  // Fetch all items then filter client-side
  // (Graph filtering on list item fields requires special indexing setup)
  let res = await client
    .api(`/sites/${siteId}/lists/${SP_CONFIG.commentsListName}/items`)
    .expand("fields")
    .orderby("createdDateTime asc")
    .top(999)
    .get();

  let items = res.value || [];
  let next = res["@odata.nextLink"];
  let guard = 0;
  while (next && guard < 50) {
    const page = await client.api(next).get();
    items = items.concat(page.value || []);
    next = page["@odata.nextLink"];
    guard++;
  }

  return items
    .filter((i) => i.fields?.POFileName === poFileName)
    .map((i) => ({
      id: i.id,
      text: i.fields.CommentText,
      author: i.fields.AuthorName,
      created: i.createdDateTime,
    }));
}

/**
 * Post a new comment on a PO file.
 */
export async function postComment(client, siteId, poFileName, vendorFolder, commentText, authorName) {
  return client
    .api(`/sites/${siteId}/lists/${SP_CONFIG.commentsListName}/items`)
    .post({
      fields: {
        POFileName: poFileName,
        VendorFolder: vendorFolder,
        CommentText: commentText,
        AuthorName: authorName,
      },
    });
}

/**
 * Post a comment that includes @mention metadata.
 * The MentionedEmails field is a comma-separated list that
 * Power Automate watches to trigger email notifications.
 */
export async function postCommentWithMentions(
  client, siteId, poFileName, vendorFolder, commentText, authorName, mentionedEmails = []
) {
  return client
    .api(`/sites/${siteId}/lists/${SP_CONFIG.commentsListName}/items`)
    .post({
      fields: {
        POFileName: poFileName,
        VendorFolder: vendorFolder,
        CommentText: commentText,
        AuthorName: authorName,
        MentionedEmails: mentionedEmails.join(","),
      },
    });
}

/**
 * Update the Status column on a PO file.
 * Uses the SharePoint listItem fields endpoint for the driveItem.
 */
export async function updatePOStatus(client, driveId, fileId, newStatus) {
  return client
    .api(`/drives/${driveId}/items/${fileId}/listItem/fields`)
    .patch({ Status: newStatus });
}

/**
 * Move a PO to the Closed folder at the root of Purchase Orders.
 */
export async function moveToClosedFolder(client, driveId, fileId) {
  // Resolve the Closed folder ID
  const closedPath = `${SP_CONFIG.poRootFolder}/${SP_CONFIG.closedFolder}`;
  const closedFolder = await client
    .api(`/drives/${driveId}/root:/${closedPath}`)
    .select("id")
    .get();

  return client.api(`/drives/${driveId}/items/${fileId}`).patch({
    parentReference: { id: closedFolder.id },
  });
}

/**
 * Create a new vendor sub-folder under the PO root folder.
 */
export async function createVendorFolder(client, driveId, folderName) {
  return client
    .api(`/drives/${driveId}/root:/${SP_CONFIG.poRootFolder}:/children`)
    .post({
      name: folderName,
      folder: {},
      "@microsoft.graph.conflictBehavior": "fail",
    });
}

/**
 * Search POs by filename and content.
 * - Returns max 20 results
 * - Prioritizes filename matches over content matches
 * - Deduplicates by filename to avoid the same PO appearing twice
 */
export async function searchPOs(client, driveId, query, vendorFolder) {
  if (!query || !query.trim()) return [];

  const q = query.trim().replace(/['"]/g, "");

  try {
    let knownFiles = [];
    if (vendorFolder) {
      knownFiles = await listPOsForVendor(client, driveId, vendorFolder);
    } else {
      const folders = await listVendorFolders(client, driveId);
      const allFiles = await Promise.all(
        folders.map((f) => listPOsForVendor(client, driveId, f.name).catch(() => []))
      );
      knownFiles = allFiles.flat();
    }

    const lq = q.toLowerCase();

    // Priority 1: filename matches (most relevant)
    const nameMatches = knownFiles.filter((f) =>
      f.name.toLowerCase().includes(lq)
    );

    // If we have enough filename matches, just return those
    if (nameMatches.length >= 3) {
      return nameMatches.slice(0, 20).map((i) => ({ ...i, fields: i.fields || {} }));
    }

    // Priority 2: content search, scoped to known PO files only
    const res = await client
      .api(`/drives/${driveId}/search(q='${encodeURIComponent(q)}')`)
      .select("id,name,size,lastModifiedDateTime,webUrl,file,parentReference")
      .top(25)
      .get();

    const knownNames = new Set(knownFiles.map((f) => f.name.toLowerCase()));
    const contentMatches = (res.value || []).filter(
      (i) =>
        i.file &&
        i.name?.toLowerCase().endsWith(".pdf") &&
        knownNames.has(i.name.toLowerCase())
    );

    // Merge, deduplicate, limit to 20
    const seen = new Set();
    const merged = [...nameMatches, ...contentMatches].filter((f) => {
      if (seen.has(f.name.toLowerCase())) return false;
      seen.add(f.name.toLowerCase());
      return true;
    });

    return merged.slice(0, 20).map((i) => ({ ...i, fields: i.fields || {} }));

  } catch (e) {
    console.error("[searchPOs] error:", e);
    return [];
  }
}

/**
 * Get thumbnail URL for a drive item.
 * SharePoint generates these automatically for PDFs.
 */
export async function getThumbnailUrl(client, driveId, fileId) {
  try {
    const res = await client
      .api(`/drives/${driveId}/items/${fileId}/thumbnails/0/medium`)
      .get();
    return res.url || null;
  } catch {
    return null;
  }
}
